"use client";
import * as React from "react";
import { getApp, type AppId } from "../apps.config";
import { cn } from "../lib/cn";

/**
 * AppIcon — the icon/color cascade primitive.
 * Renders an app's signature Lucide icon, optionally inside its accent tile.
 *
 *   <AppIcon app="finance" />                 // tile + white icon
 *   <AppIcon app="finance" tile={false} />    // bare icon in the accent color
 *   <AppIcon app="finance" size={42} radius={10} />
 */
export interface AppIconProps {
  app: AppId;
  /** Icon size in px (when tiled, the tile is ~1.9× this). Default 22. */
  size?: number;
  /** Show the rounded accent tile behind a white icon. Default true. */
  tile?: boolean;
  /** Tile corner radius in px. Default 10. */
  radius?: number;
  /** Lucide stroke width. Default 1.7. */
  strokeWidth?: number;
  className?: string;
}

export function AppIcon({
  app,
  size = 22,
  tile = true,
  radius = 10,
  strokeWidth = 1.7,
  className,
}: AppIconProps) {
  const meta = getApp(app);
  const Icon = meta.icon;

  if (!tile) {
    return (
      <Icon
        size={size}
        strokeWidth={strokeWidth}
        style={{ color: meta.accent }}
        className={className}
        aria-hidden
      />
    );
  }

  const box = Math.round(size * 1.9);
  return (
    <span
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: box, height: box, borderRadius: radius, background: meta.accent }}
      aria-hidden
    >
      <Icon size={size} strokeWidth={strokeWidth} color="#fff" />
    </span>
  );
}
